import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, ArrowRight, Linkedin, Check, Loader2, Users, Sparkles } from 'lucide-react';
import axios from 'axios';
import { podcastInfo } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SPRING = { type: 'spring', stiffness: 700, damping: 22, mass: 0.6 };
const REVEAL = { type: 'spring', stiffness: 520, damping: 26 };

const Newsletter = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    setMessage('');
    try {
      await axios.post(`${API}/subscribe`, { email, name });
      setStatus('success');
      setMessage("You're in! Welcome to The AI Revolution.");
      setEmail('');
      setName('');
    } catch (err) {
      setStatus('error');
      if (err.response && err.response.status === 409) {
        setMessage("You're already subscribed! Check your inbox.");
      } else {
        setMessage('Something went wrong. Please try again.');
      }
    }
  };

  return (
    <section id="newsletter" className="relative py-20 sm:py-32 px-4 sm:px-6 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#EF5B5B]/5 via-transparent to-transparent" />

      <div className="relative max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.97 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={REVEAL}
          className="relative rounded-3xl sm:rounded-[2rem] overflow-hidden border border-[#EF5B5B]/30"
          data-testid="newsletter-card"
          style={{
            background:
              'radial-gradient(120% 90% at 20% 0%, rgba(239,91,91,0.22), transparent 55%),' +
              'radial-gradient(120% 90% at 100% 100%, rgba(239,91,91,0.18), transparent 55%),' +
              'linear-gradient(180deg, rgba(20,10,12,0.9), rgba(10,10,10,0.95))',
            boxShadow:
              '0 0 0 1px rgba(239,91,91,0.25), 0 20px 60px -20px rgba(239,91,91,0.45), inset 0 0 80px rgba(239,91,91,0.06)',
          }}
        >
          {/* Ember blobs */}
          <motion.div
            aria-hidden
            className="absolute -top-24 -right-24 w-72 h-72 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(closest-side, rgba(239,91,91,0.55), transparent 70%)', filter: 'blur(30px)' }}
            animate={{ scale: [1, 1.08, 1], opacity: [0.75, 1, 0.75] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            aria-hidden
            className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(closest-side, rgba(239,91,91,0.35), transparent 70%)', filter: 'blur(30px)' }}
            animate={{ scale: [1, 1.12, 1], opacity: [0.6, 0.9, 0.6] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          />

          <div className="relative p-8 sm:p-14 text-center">
            {/* Social-proof badge */}
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true }}
              transition={REVEAL}
              whileHover={{ scale: 1.04 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#EF5B5B] text-white text-xs sm:text-sm font-bold tracking-wide mb-6 shadow-[0_0_24px_rgba(239,91,91,0.55)]"
              data-testid="newsletter-social-proof"
            >
              <Users className="w-4 h-4" />
              Join 1,500+ AI Revolutionaries
              <Sparkles className="w-4 h-4" />
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ ...REVEAL, delay: 0.05 }}
              className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4"
            >
              The <span className="gradient-text">AI Revolution</span>, in your inbox.
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ ...REVEAL, delay: 0.08 }}
              className="text-base sm:text-lg text-gray-300/90 mb-8 max-w-2xl mx-auto leading-relaxed"
            >
              Weekly reflections after every interview — the strategy, foresight, and human insight
              executives use to stay ahead.
            </motion.p>

            <AnimatePresence mode="wait">
              {status === 'success' ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={SPRING}
                  className="max-w-xl mx-auto"
                  data-testid="newsletter-success"
                >
                  <div className="flex items-center justify-center gap-3 px-6 py-5 rounded-2xl bg-[#EF5B5B]/15 border border-[#EF5B5B]/50 shadow-[0_0_30px_rgba(239,91,91,0.35)]">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#EF5B5B]">
                      <Check className="w-6 h-6 text-white" />
                    </div>
                    <p className="text-lg font-semibold text-white">{message}</p>
                  </div>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ ...REVEAL, delay: 0.12 }}
                  onSubmit={handleSubmit}
                  className="max-w-xl mx-auto"
                  data-testid="newsletter-form"
                >
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="email"
                      required
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="newsletter-email-input"
                      className="flex-1 px-6 py-4 rounded-full bg-black/40 border border-white/10 focus:border-[#EF5B5B]/60 text-white placeholder-gray-500 focus:outline-none smooth"
                    />
                    <motion.button
                      type="submit"
                      disabled={status === 'loading'}
                      data-testid="newsletter-submit-button"
                      whileHover={{ scale: 1.04, y: -2 }}
                      whileTap={{ scale: 0.97 }}
                      transition={SPRING}
                      className="group flex items-center justify-center gap-2 px-8 py-4 btn-red rounded-full text-base sm:text-lg font-bold whitespace-nowrap disabled:opacity-70"
                    >
                      {status === 'loading' ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Subscribing...
                        </>
                      ) : (
                        <>
                          Subscribe
                          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 smooth" />
                        </>
                      )}
                    </motion.button>
                  </div>

                  {status === 'error' && (
                    <motion.p
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={SPRING}
                      className="mt-4 text-sm text-[#EF5B5B]"
                      data-testid="newsletter-error"
                    >
                      {message}
                    </motion.p>
                  )}

                  <div className="flex items-center gap-4 my-6">
                    <div className="flex-1 h-px bg-white/10" />
                    <span className="text-xs text-gray-500 uppercase tracking-widest">or</span>
                    <div className="flex-1 h-px bg-white/10" />
                  </div>

                  <motion.a
                    href={podcastInfo.linkedinNewsletter}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="newsletter-linkedin-link"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    transition={SPRING}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-[#EF5B5B]/40 hover:border-[#EF5B5B] hover:bg-[#EF5B5B]/10 font-semibold smooth"
                  >
                    <Linkedin className="w-5 h-5 text-[#EF5B5B]" />
                    Read on LinkedIn
                  </motion.a>
                </motion.form>
              )}
            </AnimatePresence>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ ...REVEAL, delay: 0.2 }}
              className="mt-6 text-xs sm:text-sm text-gray-500"
            >
              Weekly insights · No spam, ever · Unsubscribe anytime
            </motion.p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Newsletter;
